import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircleIcon, XIcon, SendIcon, MinimizeIcon, Loader2Icon } from 'lucide-react';
import TextareaAutosize from 'react-textarea-autosize';
// Define message types
type MessageType = 'user' | 'assistant';
interface Message {
  id: string;
  type: MessageType;
  text: string;
  timestamp: Date;
}
// School knowledge base for context
const schoolKnowledgeBase = `
ISONYIN GRAMMAR SCHOOL - COMPREHENSIVE INFORMATION
BASIC INFORMATION:
- Name: Isonyin Grammar School (IGS)
- Established: 1956
- Location: Isonyin, Ijebu North East Local Government Area, Ogun State, Nigeria
- Type: Co-educational secondary school (boarding and day)
- Contact: +234 905 517 2703, info@isonyingrammarschool.ng
HISTORY & FOUNDING:
- Founded in 1956 to provide quality education to students in Isonyin and surrounding communities
- Nearly 70 years of academic excellence
- 70th Anniversary celebration planned for 2026
- Alumni network of over 15,000 graduates worldwide
ACADEMICS:
- Curriculum: Nigerian National Curriculum with enrichment programs
- Teaching Methodology: PSI/PMI pedagogy (from New Jersey Center for Teaching and Learning, U.S.A.)
- Academic Structure:
  * Junior Secondary School (JSS 1-3): Broad foundation across subjects
  * Senior Secondary School (SSS 1-3): Three tracks - Science, Commercial, Arts
- Core Subjects: Mathematics, English, Sciences, Social Studies, Languages (Yoruba, French)
- WAEC Results: 95% distinction rate, 100% pass rate in Mathematics and English
- 5-Year Performance Trend: 75% (2018) → 95% (2022) with 5 credits including English & Math
ADMISSIONS:
- Currently open for 2023/2024 academic year
- Process: Application form → Document submission → Entrance examination
- Requirements: Birth certificate, previous school records, passport photographs, medical records
- Classes: JSS1 through SS3
- Fee Structure: Varies by grade level and boarding/day option
CAMPUS FACILITIES:
- Total Area: 25+ acres of landscaped grounds
- Buildings: 15 academic and residential buildings
- Classrooms: Modern, well-ventilated with smart boards and Wi-Fi
- Science Laboratories: Separate labs for Physics, Chemistry, Biology, and Computer Science
- Library: Extensive collection with digital resources and study areas
- Sports Facilities: Football field with running track, basketball courts, volleyball courts, swimming pool, indoor sports hall
- Boarding Houses: Separate dormitories for boys and girls, capacity 500+ students
- Dining Hall: Modern cafeteria with professional catering
- Other: Assembly hall, clinic, staff quarters, Principal Quarter
STUDENT LIFE:
- Boarding: 24-hour supervision, structured daily routine, comfortable accommodation
- Sports: Football, basketball, volleyball, athletics, swimming, table tennis
- Clubs & Societies: Science Club, Debate Society, Art & Photography Club, Music & Drama Club, Environmental Club, Robotics Club
- Annual Events: Founders' Day (Feb 15), Inter-House Sports (November), Cultural Day (December), Science Fair (February), Career Day (May)
- Houses: Ransome-Kuti (Red), Awolowo (Blue), Azikiwe (Green), Bello (Yellow)
ACHIEVEMENTS:
- National Mathematics Competition: 1st Place (2022), 2nd Place (2021)
- National Science Olympiad: Top 5 Finalists (2022), Regional Champions (2021, 2020)
- Debate Competition: State Champions (2022)
- Robotics Challenge: 2nd Place National Level (2022)
- Essay Writing Contest: National Winner (2021)
ACADEMIC CALENDAR 2023/2024:
- First Term: September 11, 2023 - December 15, 2023
- Second Term: January 8, 2024 - April 12, 2024
- Third Term: April 29, 2024 - July 20, 2024
TEACHING STAFF:
- Total: 45 qualified teachers
- Qualifications: 100% hold Bachelor's degrees, 40% hold Master's or higher
- All registered with Teachers Registration Council of Nigeria (TRCN)
- Regular professional development training
ALUMNI:
- Network: 15,000+ graduates worldwide
- Active alumni association
- Notable careers: Medicine, engineering, business, law, arts, public service
- Support: Mentorship programs, infrastructure development, scholarship funds
SCHOLARSHIPS & SUPPORT:
- Merit-based scholarships available
- Need-based financial aid
- Alumni scholarship fund
- Corporate partnerships for student support
PTA (Parent-Teacher Association):
- Active involvement in school development
- Regular meetings and events
- Support for school programs and facilities
SPECIAL PROGRAMS:
- Advanced Mathematics Program
- Language Immersion (French, Yoruba)
- Science Innovation Hub
- Leadership Development Program
FEES:
- Fee structure varies by grade level and boarding/day option
- Covers: Academic instruction, learning materials, facility access
- Boarding fees include: Accommodation, meals, evening supervision
- Contact admissions office for current fee schedule
VISITING HOURS:
- Monday - Friday: 9:00 AM - 3:00 PM
- All visitors must obtain visitor's pass at main entrance
- Campus tours available by appointment
`;
const AIAssistant = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([{
    id: '1',
    type: 'assistant',
    text: "Hello! I'm your IGS virtual assistant powered by OpenAI. I have comprehensive knowledge about Isonyin Grammar School and can answer any questions about admissions, academics, facilities, student life, and more. How can I help you today?",
    timestamp: new Date()
  }]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const location = useLocation();
  const conversationHistoryRef = useRef<Array<{
    role: string;
    content: string;
  }>>([]);
  // Scroll to bottom of chat when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  }, [messages]);
  // Focus input when chat opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 300);
    }
  }, [isOpen]);
  // Add contextual message when changing pages
  useEffect(() => {
    if (messages.length > 1 && isOpen) {
      const pathName = location.pathname;
      let pageName = 'the homepage';
      if (pathName !== '/') {
        const segments = pathName.split('/').filter(Boolean);
        pageName = segments[segments.length - 1]?.replace(/-/g, ' ') || pathName;
        pageName = `the ${pageName} page`;
      }
      const contextMessage = `I see you're now on ${pageName}. I can provide specific information about this section of the website. What would you like to know?`;
      setMessages(prev => [...prev, {
        id: Date.now().toString(),
        type: 'assistant',
        text: contextMessage,
        timestamp: new Date()
      }]);
    }
  }, [location.pathname]);
  const toggleChat = () => {
    setIsOpen(!isOpen);
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };
  const callOpenAI = async (userMessage: string): Promise<string> => {
    try {
      console.log('[AI Assistant] Preparing OpenAI request...');
      // Get current page context
      const currentPath = location.pathname;
      let pageContext = '';
      if (currentPath.includes('admissions')) {
        pageContext = 'The user is currently viewing the Admissions page.';
      } else if (currentPath.includes('academics')) {
        pageContext = 'The user is currently viewing the Academics page.';
      } else if (currentPath.includes('campus')) {
        pageContext = 'The user is currently viewing the Campus page.';
      } else if (currentPath.includes('student-life')) {
        pageContext = 'The user is currently viewing the Student Life page.';
      } else if (currentPath.includes('alumni')) {
        pageContext = 'The user is currently viewing the Alumni page.';
      } else if (currentPath.includes('contact')) {
        pageContext = 'The user is currently viewing the Contact page.';
      }
      // Build system message with context
      const systemMessage = {
        role: 'system',
        content: `You are an intelligent virtual assistant for Isonyin Grammar School (IGS), a prestigious secondary school in Nigeria established in 1956. Your role is to provide accurate, helpful, and friendly information about the school.
IMPORTANT GUIDELINES:
1. Use the provided school knowledge base to answer questions accurately
2. Be conversational, warm, and professional
3. If asked about information not in the knowledge base, politely redirect the user to contact the school directly at +234 905 517 2703 or info@isonyingrammarschool.ng
4. Provide specific details when available (dates, numbers, names, etc.)
5. Keep responses concise but comprehensive (aim for 2-3 paragraphs)
6. If the user's question is unclear, ask clarifying questions
7. Suggest relevant pages or sections of the website when appropriate
8. Always maintain a positive tone about the school
${pageContext}
SCHOOL KNOWLEDGE BASE:
${schoolKnowledgeBase}
Remember: If you don't have specific information, it's better to direct the user to contact the school rather than making up information.`
      };
      // Add user message to conversation history
      conversationHistoryRef.current.push({
        role: 'user',
        content: userMessage
      });
      // Prepare messages array for API call (keep last 10 exchanges for context)
      const apiMessages = [systemMessage, ...conversationHistoryRef.current.slice(-10)];
      console.log('[AI Assistant] Sending request to OpenAI proxy...');
      console.log('[AI Assistant] Message count:', apiMessages.length);
      // Call OpenAI through Magic Patterns proxy
      const response = await fetch('https://requests.magicpatterns.com/openai/proxy', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          model: 'gpt-4-turbo',
          messages: apiMessages,
          temperature: 0.7,
          max_tokens: 500
        })
      });
      console.log('[AI Assistant] Response status:', response.status);
      if (!response.ok) {
        const errorText = await response.text();
        console.error('[AI Assistant] API Error:', {
          status: response.status,
          statusText: response.statusText,
          body: errorText
        });
        throw new Error(`API request failed: ${response.status} ${response.statusText}`);
      }
      const data = await response.json();
      console.log('[AI Assistant] Response received successfully');
      // Validate response structure
      if (!data || !data.choices || !data.choices[0] || !data.choices[0].message) {
        console.error('[AI Assistant] Invalid response structure:', data);
        throw new Error('Invalid response structure from OpenAI');
      }
      const assistantMessage = data.choices[0].message.content;
      // Add assistant response to conversation history
      conversationHistoryRef.current.push({
        role: 'assistant',
        content: assistantMessage
      });
      console.log('[AI Assistant] Response processed successfully');
      return assistantMessage;
    } catch (error) {
      console.error('[AI Assistant] Error:', error);
      // Return user-friendly error message
      if (error instanceof Error) {
        if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
          return "I'm having trouble connecting to my AI service. Please check your internet connection and try again, or contact the school directly at +234 905 517 2703 or info@isonyingrammarschool.ng for immediate assistance.";
        }
        return `I apologize, but I encountered an error: ${error.message}. Please try again in a moment, or contact the school directly at +234 905 517 2703 or info@isonyingrammarschool.ng for immediate assistance.`;
      }
      return "I apologize, but I'm having trouble connecting right now. Please try again in a moment, or contact the school directly at +234 905 517 2703 or info@isonyingrammarschool.ng for immediate assistance.";
    }
  };
  const handleSendMessage = async () => {
    if (inputValue.trim() === '') return;
    // Add user message to chat
    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      text: inputValue.trim(),
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);
    // Get AI response
    try {
      const response = await callOpenAI(userMessage.text);
      setMessages(prev => [...prev, {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        text: response,
        timestamp: new Date()
      }]);
    } catch (error) {
      console.error('[AI Assistant] Error in handleSendMessage:', error);
      setMessages(prev => [...prev, {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        text: 'I apologize for the inconvenience. Please contact the school directly at +234 905 517 2703 or info@isonyingrammarschool.ng for assistance.',
        timestamp: new Date()
      }]);
    } finally {
      setIsTyping(false);
    }
  };
  return <>
      {/* Floating chat button */}
      <motion.button className="fixed bottom-6 right-6 bg-blue-800 text-white p-4 rounded-full shadow-lg z-50 flex items-center justify-center hover:bg-blue-900 transition-colors" onClick={toggleChat} whileHover={{
      scale: 1.1
    }} whileTap={{
      scale: 0.95
    }} initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 1
    }} aria-label="Open AI Assistant" data-id="element-525">
        {isOpen ? <MinimizeIcon className="h-6 w-6" data-id="element-526" /> : <MessageCircleIcon className="h-6 w-6" data-id="element-527" />}
      </motion.button>
      {/* Chat window */}
      <AnimatePresence data-id="element-528">
        {isOpen && <motion.div className="fixed bottom-24 right-6 w-80 sm:w-96 bg-white rounded-lg shadow-xl z-50 flex flex-col overflow-hidden" style={{
        maxHeight: 'calc(100vh - 120px)'
      }} initial={{
        opacity: 0,
        y: 50,
        scale: 0.9
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 50,
        scale: 0.9
      }} transition={{
        type: 'spring',
        damping: 20,
        stiffness: 300
      }} data-id="element-529">
            {/* Chat header */}
            <div className="bg-blue-900 text-white p-4 flex items-center justify-between" data-id="element-530">
              <div className="flex items-center" data-id="element-531">
                <div className="bg-yellow-500 text-blue-900 rounded-full w-8 h-8 flex items-center justify-center mr-3 font-bold" data-id="element-532">
                  IGS
                </div>
                <div data-id="element-533">
                  <h3 className="font-bold" data-id="element-534">IGS AI Assistant</h3>
                  <p className="text-xs text-blue-100" data-id="element-535">
                    Powered by OpenAI GPT-4
                  </p>
                </div>
              </div>
              <button onClick={toggleChat} className="text-white hover:text-gray-200 transition-colors" aria-label="Close chat" data-id="element-536">
                <XIcon className="h-5 w-5" data-id="element-537" />
              </button>
            </div>
            {/* Chat messages */}
            <div className="flex-1 p-4 overflow-y-auto bg-gray-50" data-id="element-538">
              <div className="space-y-4" data-id="element-539">
                {messages.map(message => <motion.div key={message.id} className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.3
            }} data-id="element-540">
                    <div className={`max-w-[80%] p-3 rounded-lg ${message.type === 'user' ? 'bg-blue-700 text-white rounded-tr-none' : 'bg-white border border-gray-200 rounded-tl-none'}`} data-id="element-541">
                      <p className="text-sm whitespace-pre-wrap" data-id="element-542">
                        {message.text}
                      </p>
                      <p className={`text-xs mt-1 ${message.type === 'user' ? 'text-blue-200' : 'text-gray-500'}`} data-id="element-543">
                        {message.timestamp.toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                      </p>
                    </div>
                  </motion.div>)}
                {isTyping && <motion.div className="flex justify-start" initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} data-id="element-544">
                    <div className="bg-white border border-gray-200 p-3 rounded-lg rounded-tl-none max-w-[80%]" data-id="element-545">
                      <div className="flex items-center space-x-2" data-id="element-546">
                        <Loader2Icon className="h-4 w-4 text-blue-700 animate-spin" data-id="element-547" />
                        <p className="text-sm text-gray-500" data-id="element-548">
                          AI is thinking...
                        </p>
                      </div>
                    </div>
                  </motion.div>}
                <div ref={messagesEndRef} data-id="element-549" />
              </div>
            </div>
            {/* Chat input */}
            <div className="border-t border-gray-200 p-3 bg-white" data-id="element-550">
              <div className="flex items-end gap-2" data-id="element-551">
                <div className="flex-1 bg-gray-100 rounded-lg px-3 py-2" data-id="element-552">
                  <TextareaAutosize ref={inputRef} value={inputValue} onChange={handleInputChange} onKeyDown={handleKeyDown} placeholder="Ask me anything about IGS..." className="w-full bg-transparent resize-none focus:outline-none text-sm max-h-32" maxRows={4} data-id="element-553" />
                </div>
                <motion.button onClick={handleSendMessage} disabled={inputValue.trim() === '' || isTyping} className={`p-2 rounded-full transition-colors ${inputValue.trim() === '' || isTyping ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-blue-700 text-white hover:bg-blue-800'}`} whileHover={inputValue.trim() !== '' && !isTyping ? {
              scale: 1.1
            } : {}} whileTap={inputValue.trim() !== '' && !isTyping ? {
              scale: 0.95
            } : {}} aria-label="Send message" data-id="element-554">
                  <SendIcon className="h-5 w-5" data-id="element-555" />
                </motion.button>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </>;
};
export default AIAssistant;